import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

/**
 * @typedef {Object} CustomNavigateOptions
 * @property {Object.<string, string|string[]>} [params] - Optional query parameters to append.
 * @property {boolean} [replace] - Whether to replace the current entry in the history stack.
 * @property {string} [state] - Optional state to attach to navigation.
 */

/**
 * Custom navigation hook to navigate with query parameters.
 * @returns {(url: string, options?: CustomNavigateOptions) => void}
 */
export function useNav() {
  const navigate = useNavigate()

  /**
   * Navigate to a URL with optional query parameters.
   * @param {string} url - The destination URL.
   * @param {CustomNavigateOptions} [options] - Navigation options.
   */
  const customNavigate = (url, options) => {
    let paramsString = ''
    if (options?.params) {
      const searchParams = new URLSearchParams()

      Object.entries(options.params).forEach(([key, value]) => {
        if (!value) return
        if (Array.isArray(value)) {
          value.forEach((v) => searchParams.append(key, v))
        } else {
          searchParams.append(key, value)
        }
      })

      paramsString = searchParams.toString()
    }
    navigate({ pathname: url, search: paramsString }, options)
  }

  return customNavigate
}

/**
 * Hook to parse query parameters from the current URL.
 * @returns {{ queryParams: Object.<string, string> }}
 */
export function useParseQuery() {
  const location = useLocation()
  const [queryParams, setQueryParams] = useState({})

  /**
   * Parse the query string into an object.
   * @param {string} search - The query string from the URL.
   * @returns {Object.<string, string>} - Parsed query parameters.
   */
  const parseQueryParams = (search) => {
    const params = new URLSearchParams(search)
    const queryObject = {}
    params.forEach((value, key) => {
      queryObject[key] = value
    })
    return queryObject
  }

  useEffect(() => {
    setQueryParams(parseQueryParams(location.search))
  }, [location.search])

  return { queryParams }
}
